import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RealtimeEvents, type TripStatus } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { MapView } from '../components/MapView.js';
import { OnlineToggle } from '../components/OnlineToggle.js';
import { OfferCard } from '../components/OfferCard.js';
import { StageControls } from '../components/StageControls.js';
import { useSession } from '../state/session.js';
import { useOffer } from '../state/offer.js';
import { useDriverSocket } from '../hooks/useDriverSocket.js';
import { ApiError } from '../api/client.js';
import { getTrip, pushLocation, setStatus } from '../api/driver.js';
import { useToast } from '../components/Toast.js';

const HOME_CENTRE = { lat: 24.8607, lng: 67.0011 };
const LOCATION_INTERVAL_MS = 4000;

export function DrivePage() {
  const socket = useDriverSocket();
  const queryClient = useQueryClient();
  const toast = useToast();

  const online = useSession((s) => s.online);
  const setOnline = useSession((s) => s.setOnline);

  const offer = useOffer((s) => s.offer);
  const setOffer = useOffer((s) => s.setOffer);
  const activeTripId = useOffer((s) => s.activeTripId);
  const setActiveTripId = useOffer((s) => s.setActiveTripId);

  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Best-effort browser geolocation when online; falls back to a static city centre
  // so the demo works on desktops that don't expose GPS.
  useEffect(() => {
    if (!online || !navigator.geolocation) return;
    let watchId: number | null = null;
    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => undefined,
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 },
      );
    } catch {
      /* watch unsupported; ignore */
    }
    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, [online]);

  // Throttled position push: REST + socket so the server and admin see us promptly.
  useEffect(() => {
    if (!online || !position) return;
    const tick = () => {
      pushLocation(position.lat, position.lng).catch(() => undefined);
      socket?.emit(RealtimeEvents.DriverLocation, { lat: position.lat, lng: position.lng });
    };
    tick();
    const id = setInterval(tick, LOCATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, [online, position, socket]);

  const statusMutation = useMutation({
    mutationFn: setStatus,
    onSuccess: (res) => {
      const isOnline = res.status === 'online' || res.status === 'on_trip';
      setOnline(isOnline);
      toast.show(isOnline ? "You're online. Drive safely." : 'You are now offline.', isOnline ? 'success' : 'info');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Could not update your status.';
      setError(msg);
      toast.show(msg, 'error');
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!offer || !socket) throw new Error('No active offer.');
      socket.emit(RealtimeEvents.TripAccept, { tripId: offer.tripId });
      setActiveTripId(offer.tripId);
      setOffer(null);
      toast.show('Accepted. Head to the pickup point.', 'success');
    },
  });

  const declineMutation = useMutation({
    mutationFn: async () => {
      if (!offer || !socket) return;
      socket.emit(RealtimeEvents.TripDecline, { tripId: offer.tripId });
      setOffer(null);
      toast.show('Request declined.', 'info');
    },
  });

  const activeTrip = useQuery({
    queryKey: ['active-trip', activeTripId],
    enabled: Boolean(activeTripId),
    queryFn: () => getTrip(activeTripId as string),
    refetchInterval: (q) => {
      const s = (q.state.data?.status as TripStatus | undefined);
      if (!s || s === 'completed' || s === 'cancelled') return false;
      return 4000;
    },
  });

  // Subscribe to live trip updates so the stage card moves in step with the server.
  useEffect(() => {
    if (!socket || !activeTripId) return;
    socket.emit('trip:join', { tripId: activeTripId });
    const onUpdate = () => queryClient.invalidateQueries({ queryKey: ['active-trip', activeTripId] });
    socket.on(RealtimeEvents.TripUpdate, onUpdate);
    socket.on(RealtimeEvents.TripFare, onUpdate);
    return () => {
      socket.emit('trip:leave', { tripId: activeTripId });
      socket.off(RealtimeEvents.TripUpdate, onUpdate);
      socket.off(RealtimeEvents.TripFare, onUpdate);
    };
  }, [socket, activeTripId, queryClient]);

  // Hide the stage card and refresh earnings once a trip ends.
  useEffect(() => {
    if (!activeTrip.data) return;
    const s = activeTrip.data.status as TripStatus;
    if (s === 'completed' || s === 'cancelled') {
      queryClient.invalidateQueries({ queryKey: ['earnings'] });
      const t = setTimeout(() => setActiveTripId(null), 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [activeTrip.data, queryClient, setActiveTripId]);

  const routeTo = useMemo(() => {
    if (!activeTrip.data) return null;
    return activeTrip.data.status === 'in_progress' ? activeTrip.data.drop : activeTrip.data.pickup;
  }, [activeTrip.data]);

  const onArrived = () => {
    socket?.emit(RealtimeEvents.TripArrived, { tripId: activeTripId });
    toast.show('Marked as arrived. Waiting for the rider.', 'info');
  };
  const onStart = () => {
    socket?.emit(RealtimeEvents.TripStart, { tripId: activeTripId });
    toast.show('Trip started.', 'success');
  };
  const onComplete = () => {
    socket?.emit(RealtimeEvents.TripComplete, { tripId: activeTripId });
    toast.show('Trip completed.', 'success');
  };

  return (
    <Shell fullBleed>
      <div className="absolute inset-0">
        <MapView
          centre={position ?? HOME_CENTRE}
          me={online ? position : null}
          pickup={activeTrip.data?.pickup}
          drop={activeTrip.data?.drop}
          routeTo={online ? routeTo : null}
        />
      </div>

      <header className="pointer-events-none absolute inset-x-0 top-0 z-[1000] flex justify-center px-4 pt-[max(env(safe-area-inset-top),12px)]">
        <div className="pointer-events-auto flex w-full max-w-mobile items-center justify-between rounded-2xl border border-line bg-surface/95 px-4 py-2 shadow-card backdrop-blur">
          <p className="font-display text-lg font-bold">Raahi Driver</p>
          <span className="chip">{online ? 'Online' : 'Offline'}</span>
        </div>
      </header>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1000] flex justify-center pb-[max(env(safe-area-inset-bottom),16px)]">
        <div className="pointer-events-auto w-full max-w-mobile space-y-3 px-3">
          {error ? (
            <p role="alert" className="rounded-xl border border-red-soft/40 bg-red-soft/10 px-3 py-2 text-sm text-red-soft">
              {error}
            </p>
          ) : null}

          {activeTrip.data ? (
            <StageControls
              trip={activeTrip.data}
              onArrived={onArrived}
              onStart={onStart}
              onComplete={onComplete}
            />
          ) : offer ? (
            <OfferCard
              offer={offer}
              onAccept={() => acceptMutation.mutate()}
              onDecline={() => declineMutation.mutate()}
              busy={acceptMutation.isPending}
            />
          ) : (
            <OnlineToggle
              online={online}
              busy={statusMutation.isPending}
              onToggle={() => {
                setError(null);
                statusMutation.mutate(online ? 'offline' : 'online');
              }}
            />
          )}
        </div>
      </div>
    </Shell>
  );
}
